import { 
  GoogleAdsCampaign, 
  GoogleAdsConnection, 
  CampaignDraft,
  OptimizationSuggestion,
  CampaignPerformanceHistory,
  LandingPageAnalysis,
  CampaignType,
  CampaignStatus,
  DetectedConversion
} from '../types/googleAds';
import { db } from '../config/firebase';
import { ragService } from './ragService';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { openaiService } from './openaiService';
import { googleAdsApiClient } from './googleAdsApiClient';
import { googleAdsMccService } from './googleAdsMccService';
import { impressumParser, BusinessInfo } from './impressumParser';
import { geminiService } from './geminiService';
import { firecrawlService } from './firecrawlService';
import { conversionTagParser } from './conversionTagParser';
import { languageDetector } from './languageDetector';
import { industryDetector } from './industryDetector';

/**
 * Google Ads Service
 * Handles campaign management with both real Google Ads API and demo mode
 */
export const googleAdsService = {
  /**
   * Map Google Ads API status to our internal status
   * Google Ads uses: ENABLED, PAUSED, REMOVED
   * We use: active, paused, ended
   */
  mapGoogleAdsStatus(apiStatus: string): 'active' | 'paused' | 'draft' | 'ended' {
    const status = apiStatus.toUpperCase();
    switch (status) {
      case 'ENABLED':
        return 'active';
      case 'PAUSED':
        return 'paused';
      case 'REMOVED':
        return 'ended';
      default:
        return 'draft';
    }
  },

  /**
   * Check if Google Ads is connected for an organization
   */
  async checkConnection(orgId: string): Promise<GoogleAdsConnection> {
    try {
      const connectionRef = doc(db, 'organizations', orgId, 'integrations', 'googleAds');
      const connectionDoc = await getDoc(connectionRef);
      
      if (connectionDoc.exists()) {
        const data = connectionDoc.data();
        return {
          orgId,
          isConnected: data.isConnected || false,
          customerId: data.customerId,
          accountName: data.accountName,
          connectedAt: data.connectedAt?.toDate(),
          lastSyncAt: data.lastSyncAt?.toDate(),
          apiVersion: data.apiVersion,
          status: data.status || 'disconnected',
          errorMessage: data.errorMessage
        };
      }
      
      // Default: Demo mode
      return {
        orgId,
        isConnected: false,
        status: 'demo'
      };
    } catch (error) {
      console.error('Error checking Google Ads connection:', error);
      return {
        orgId,
        isConnected: false,
        status: 'error',
        errorMessage: 'Failed to check connection status'
      };
    }
  },

  /**
   * Connect to Google Ads (OAuth flow - placeholder for now)
   */
  /**
   * Get a quick summary of campaigns for a customer ID
   * (Used by CI Dashboard)
   */
  async getCampaignSummary(orgId: string, customerId: string): Promise<{
    activeCampaigns: number;
    totalSpend: number;
    totalConversions: number;
    currency: string;
  }> {
    try {
      const campaignsRef = collection(db, 'organizations', orgId, 'googleAdsCampaigns');
      const q = query(campaignsRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      
      let totalSpend = 0;
      let totalConversions = 0;
      let activeCampaigns = 0;
      
      snapshot.forEach(doc => {
        const data = doc.data();
        activeCampaigns++;
        totalSpend += data.metrics?.cost || 0;
        totalConversions += data.metrics?.conversions || 0;
      });
      
      return {
        activeCampaigns,
        totalSpend,
        totalConversions,
        currency: 'EUR'
      };
    } catch (error) {
      console.error('Error fetching campaign summary:', error);
      return { activeCampaigns: 0, totalSpend: 0, totalConversions: 0, currency: 'EUR' };
    }
  },

  /**
   * Feedback Loop: Capture high-performing keywords and save to Knowledge Base (RAG)
   */
  async captureProvenKeywords(orgId: string, industry: string, keywords: Array<{ text: string; ctr: number }>) {
    try {
      // Filter for high CTR (e.g., > 5%)
      const winners = keywords.filter(kw => kw.ctr > 0.05);
      
      for (const winner of winners) {
        await ragService.saveKeywordToKnowledgeBase(orgId, {
          keyword: winner.text,
          industry: industry,
          performanceScore: Math.min(100, winner.ctr * 1000), // Scale CTR to 0-100 score
          metadata: { source: 'campaign_feedback', ctr: winner.ctr }
        });
      }
      
      if (winners.length > 0) {
        console.log(`📈 RAG: Captured ${winners.length} winner keywords for ${industry}`);
      }
    } catch (error) {
      console.error('Error in RAG feedback loop:', error);
    }
  },

  async connectGoogleAds(orgId: string, credentials: { customerId: string }): Promise<void> {
    try {
      const connectionRef = doc(db, 'organizations', orgId, 'integrations', 'googleAds');
      
      await setDoc(connectionRef, {
        isConnected: true,
        customerId: credentials.customerId,
        connectedAt: Timestamp.now(),
        status: 'connected',
        apiVersion: 'v14'
      });
      
      console.log('✅ Google Ads connected successfully');
    } catch (error) {
      console.error('Error connecting Google Ads:', error);
      throw new Error('Failed to connect Google Ads');
    }
  },

  /**
   * Disconnect Google Ads
   */
  async disconnectGoogleAds(orgId: string): Promise<void> {
    try {
      const connectionRef = doc(db, 'organizations', orgId, 'integrations', 'googleAds');
      
      await updateDoc(connectionRef, {
        isConnected: false,
        status: 'disconnected',
        disconnectedAt: Timestamp.now()
      });
      
      console.log('✅ Google Ads disconnected');
    } catch (error) {
      console.error('Error disconnecting Google Ads:', error);
      throw new Error('Failed to disconnect Google Ads');
    }
  },

  /**
   * Analyze landing page using REAL web scraping - FIRECRAWL ONLY
   * 
   * Architecture:
   * 1. Firecrawl scrapes HTML + Markdown (REAL data)
   * 2. Direct HTML parsing for tracking tags, forms, images
   * 3. Gemini analyzes real scraped content (interpretation only)
   * 
   * NO FALLBACKS - NO FAKE DATA!
   * If Firecrawl fails or not configured → throw error
   */
  async analyzeLandingPage(url: string): Promise<LandingPageAnalysis & { businessInfo?: BusinessInfo }> {
    try {
      // PRIMARY: FIRECRAWL for REAL scraping (no hallucinations!)
      if (firecrawlService.isConfigured()) {
        console.log('🔥 Step 1: Scraping website with Firecrawl (REAL data)...');
        
        try {
          // STEP 1: REAL scraping with Firecrawl
          const scrapedData = await firecrawlService.scrapeWebsite(url);
          
          console.log(`✅ Firecrawl scraped ${scrapedData.markdown.length} chars of REAL content`);
          console.log(`✅ Found ${scrapedData.images.length} REAL image URLs`);
          
          // STEP 2: Find REAL conversions from HTML
          const realConversions = await firecrawlService.findRealConversions(url);
          console.log(`✅ Found ${realConversions.length} REAL conversion forms`);
          
          // STEP 3: Extract REAL Impressum - Try specific pages first!
          console.log('📍 Step 3: Searching for Impressum...');
          let businessInfo = await impressumParser.findAndExtractBusinessInfo(url);
          
          if (!businessInfo) {
            // Fallback: Try main page footer
            console.log('  ℹ️ Trying main page footer...');
            businessInfo = await impressumParser.extractBusinessInfoFromScrapedContent(
              scrapedData.html,
              scrapedData.markdown,
              url
            );
          }
          
          if (businessInfo?.city) {
            console.log(`✅ Business info found: ${businessInfo.businessName} in ${businessInfo.city}, ${businessInfo.country}`);
            if (businessInfo.vatId) console.log(`   VAT ID: ${businessInfo.vatId}`);
          } else {
            console.log('⚠️ No business info found');
          }
          
          // STEP 4: Parse REAL Conversion Tags from HTML
          console.log('🏷️ Step 4: Parsing conversion tracking tags...');
          const trackingAnalysis = conversionTagParser.parseConversionTags(scrapedData.html);
          console.log(`✅ Found ${trackingAnalysis.conversionTags.length} tracking tags`);
          console.log(`Platform: ${trackingAnalysis.platform}`);
          
          // STEP 5: Extract Du/Sie Tonality (Direct Regex - No AI!)
          console.log('🎯 Step 5: Detecting tonality (Du/Sie)...');
          const tonalityAnalysis = firecrawlService.detectTonality(scrapedData.markdown, scrapedData.html);
          console.log(`✅ Tonality: ${tonalityAnalysis.addressForm} (${tonalityAnalysis.confidence}% confidence)`);
          
          // STEP 6: Extract All Languages (Direct Regex - No AI!)
          console.log('🌐 Step 6: Detecting all languages...');
          const allLanguages = firecrawlService.extractAllLanguages(scrapedData.html, scrapedData.markdown);
          console.log(`✅ Languages found: ${allLanguages.join(', ')}`);
          
          // STEP 7: Extract SEO Metadata (Direct HTML Parsing - No AI!)
          console.log('📊 Step 7: Extracting SEO metadata...');
          const seoMetadata = firecrawlService.extractSEOMetadata(scrapedData.html, scrapedData.metadata);
          console.log(`✅ SEO: ${seoMetadata.headings.h1.length} H1s, ${seoMetadata.keywords.length} keywords`);
          
          // STEP 8: Smart Language Detection (Domain-based override)
          console.log('🌐 Step 8: Smart language detection...');
          const languageResult = languageDetector.detectCampaignLanguage(
            url,
            scrapedData.language,
            allLanguages
          );
          console.log(`✅ Campaign Language: ${languageResult.campaignLanguageName} (${languageResult.reasoning})`);
          if (languageResult.hasLanguageMismatch) {
            console.warn(`⚠️ Language Mismatch: Website is ${languageResult.contentLanguage}, Campaign uses ${languageResult.campaignLanguage}`);
          }
          
          // STEP 9: Gemini analyzes the REAL scraped content (interpretation only!)
          console.log('🤖 Step 9: Gemini analyzing REAL content for industry/keywords...');
          // Override metadata language with campaign language for correct keyword generation
          const metadataWithCampaignLang = {
            ...scrapedData.metadata,
            language: languageResult.campaignLanguage  // Use campaign language, not content language!
          };
          const geminiAnalysis = await geminiService.analyzeScrapedContent(
            scrapedData.markdown,
            scrapedData.images,
            metadataWithCampaignLang,
            url
          );
          
          // STEP 9.5: Vision AI Image Analysis (parallel-safe, non-blocking)
          console.log('🖼️ Step 9.5: Analyzing images with Vision AI...');
          let imageAnalysis = null;
          try {
            imageAnalysis = await geminiService.analyzeProductImages(
              scrapedData.images.slice(0, 5),
              { 
                industry: geminiAnalysis.industry, 
                businessName: geminiAnalysis.businessName 
              }
            );
            console.log(`✅ Vision AI: ${imageAnalysis.detectedProducts.length} products, ${imageAnalysis.detectedServices.length} services detected`);
          } catch (visionError) {
            console.log('⚠️ Vision AI analysis skipped:', visionError);
          }
          
          // STEP 10: Multi-Layer Industry Detection
          console.log('🏭 Step 10: Multi-layer industry detection...');
          const industryResult = await industryDetector.detectIndustry(
            url,
            scrapedData.markdown,
            scrapedData.metadata,
            geminiAnalysis
          );
          console.log(`✅ Industry: ${industryResult.industry} (${industryResult.confidence}% via ${industryResult.method})`);
          console.log(`   Evidence: ${industryResult.evidence.join(', ')}`);
          
          // STEP 11: Build complete analysis from REAL data
          console.log('📦 Step 11: Building complete analysis...');
          const analysis: LandingPageAnalysis & { businessInfo?: BusinessInfo; languageWarning?: string } = {
            url,
            title: scrapedData.title || geminiAnalysis.title,
            industry: industryResult.industry,    // Use multi-layer detection!
            valueProposition: geminiAnalysis.valueProposition,
            targetAudience: geminiAnalysis.targetAudience,
            keyBenefits: geminiAnalysis.keyBenefits || [],
            features: geminiAnalysis.features || [],
            callToAction: geminiAnalysis.callToAction,
            
            // Language Detection (Enhanced - Smart Domain-based)
            detectedLanguage: languageResult.campaignLanguage,        // Smart-detected for campaign
            detectedLanguageName: languageResult.campaignLanguageName,
            detectedLanguages: allLanguages.length > 0 ? allLanguages : [scrapedData.language],
            languageWarning: languageResult.hasLanguageMismatch 
              ? `Website ist ${languageDetector.getLanguageName(languageResult.contentLanguage)}, Kampagne nutzt ${languageResult.campaignLanguageName} für Zielgruppe`
              : undefined,
            
            // Tonality Detection (NEW - Du vs. Sie)
            tonality: {
              formality: tonalityAnalysis.formality,
              addressForm: tonalityAnalysis.addressForm,
              confidence: tonalityAnalysis.confidence,
              evidence: tonalityAnalysis.evidence,
              recommendation: tonalityAnalysis.recommendation
            },
            
            // REAL conversions from HTML parsing!
            detectedConversions: realConversions.map(conv => ({
              type: conv.type,
              name: `${conv.type.charAt(0).toUpperCase() + conv.type.slice(1)} Form`,
              value: this.estimateConversionValue(geminiAnalysis.industry, conv.type),
              confidence: 100,  // 100% because actually found in HTML!
              description: conv.evidence,
              selector: conv.formId || 'form',
              evidence: conv.evidence
            })),
            suggestedKeywords: geminiAnalysis.suggestedKeywords || [],
            tone: geminiAnalysis.tone || 'professional',
            recommendedCampaignType: geminiAnalysis.recommendedCampaignType || 'search',
            recommendedBudget: geminiAnalysis.recommendedBudget || { min: 20, max: 200, recommended: 50 },
            hasEcommerce: realConversions.some(c => c.type === 'purchase'),
            hasForms: realConversions.length > 0,
            
            // Business Info (with tax IDs!)
            businessInfo: businessInfo || geminiAnalysis.businessInfo,  // Use REAL Impressum data!
            
            // Tracking Tags
            trackingTags: {
              hasConversionTracking: trackingAnalysis.hasTracking,
              googleTagManager: trackingAnalysis.gtmId,
              googleAnalytics: trackingAnalysis.ga4Id,
              googleAdsConversion: trackingAnalysis.googleAdsId,
              facebookPixel: trackingAnalysis.fbPixelId,
              detectedEvents: trackingAnalysis.conversionTags.map(t => t.eventName || t.conversionLabel).filter(Boolean) as string[]
            },
            
            // SEO Metadata (NEW)
            seoMetadata: {
              title: seoMetadata.title,
              description: seoMetadata.description,
              keywords: seoMetadata.keywords,
              headings: seoMetadata.headings,
              canonicalUrl: seoMetadata.canonicalUrl,
              ogTitle: seoMetadata.ogTitle,
              ogDescription: seoMetadata.ogDescription,
              ogImage: seoMetadata.ogImage,
              ogType: seoMetadata.ogType,
              structuredData: seoMetadata.structuredData,
              robots: seoMetadata.robots
            },
            
            // Images (Enhanced)
            imageUrls: scrapedData.images,  // NEW: Real image URLs from Firecrawl
            logoUrl: geminiAnalysis.logoUrl,  // NEW: Detected logo
            
            // Business Context (from Gemini analysis)
            businessContext: geminiAnalysis.businessContext ? {
              businessModel: geminiAnalysis.businessContext.businessModel || 'unknown',
              usp: geminiAnalysis.businessContext.usp || geminiAnalysis.valueProposition || '',
              slogan: geminiAnalysis.businessContext.slogan,
              offerings: geminiAnalysis.businessContext.offerings || [],
              competitors: geminiAnalysis.businessContext.competitors,
              industryConfidence: geminiAnalysis.industryConfidence || industryResult.confidence,
              industryReasoning: geminiAnalysis.industryReasoning || industryResult.evidence.join(', '),
              alternativeIndustries: geminiAnalysis.alternativeIndustries
            } : undefined,
            
            // Brand Identity (from Gemini analysis)
            brandIdentity: geminiAnalysis.brandIdentity ? {
              tone: geminiAnalysis.brandIdentity.tone || 'formal',
              toneEvidence: geminiAnalysis.brandIdentity.toneEvidence || '',
              addressForm: geminiAnalysis.brandIdentity.addressForm || tonalityAnalysis.addressForm,
              colors: geminiAnalysis.brandIdentity.colors || [],
              primaryColor: geminiAnalysis.brandIdentity.primaryColor,
              brandImages: scrapedData.images.slice(0, 10)
            } : undefined,
            
            // Image Analysis (from Vision AI)
            imageAnalysis: imageAnalysis ? {
              analyzedImages: imageAnalysis.analyzedImages,
              detectedProducts: imageAnalysis.detectedProducts,
              detectedServices: imageAnalysis.detectedServices,
              visualStyle: imageAnalysis.visualStyle,
              dominantColors: imageAnalysis.dominantColors,
              atmosphere: imageAnalysis.atmosphere,
              suggestedImageDescriptions: imageAnalysis.suggestedImageDescriptions
            } : undefined,
            
            // Competitor Context (from Gemini analysis)
            competitorContext: geminiAnalysis.competitorContext ? {
              mentionedCompetitors: geminiAnalysis.competitorContext.mentionedCompetitors || [],
              marketPosition: geminiAnalysis.competitorContext.marketPosition || '',
              differentiators: geminiAnalysis.competitorContext.differentiators || [],
              avoidPhrases: geminiAnalysis.competitorContext.avoidPhrases || [],
              uniqueAdAngles: geminiAnalysis.competitorContext.uniqueAdAngles || [],
              competitiveAdvantages: geminiAnalysis.competitorContext.competitiveAdvantages || []
            } : undefined,
            
            analyzedAt: new Date()
          };
          
          // Console logging for all extracted data
          console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
          console.log(`✅ WEBSITE ANALYSIS COMPLETE (100% REAL DATA)`);
          console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
          console.log(`🏢 Business: ${businessInfo?.businessName || 'Unknown'}`);
          console.log(`📍 Location: ${businessInfo?.city || 'Unknown'}, ${businessInfo?.country || 'Unknown'}`);
          if (businessInfo?.vatId) console.log(`💼 VAT ID: ${businessInfo.vatId}`);
          if (businessInfo?.taxId) console.log(`💼 Tax ID: ${businessInfo.taxId}`);
          console.log(`🏭 Industry: ${analysis.industry}`);
          console.log(`🌐 Languages: ${allLanguages.join(', ')} (Primary: ${analysis.detectedLanguageName})`);
          console.log(`🎯 Tonality: ${tonalityAnalysis.addressForm}-Form (${tonalityAnalysis.confidence}%)`);
          console.log(`   → ${tonalityAnalysis.recommendation}`);
          console.log(`📊 SEO: ${seoMetadata.headings.h1.length} H1s, ${seoMetadata.headings.h2.length} H2s, ${seoMetadata.keywords.length} meta keywords`);
          console.log(`🖼️  Images: ${scrapedData.images.length} found`);
          console.log(`📝 Forms: ${realConversions.length} conversion points`);
          
          if (trackingAnalysis.hasTracking) {
            console.log(`\n🏷️ CONVERSION TRACKING FOUND:`);
            trackingAnalysis.conversionTags.forEach(tag => {
              console.log(`  - ${tag.platform}: ${tag.tagId}`);
              console.log(`    Type: ${tag.type}`);
              console.log(`    Can be used: ${tag.canBeUsed ? '✅' : '❌'} ${tag.reason || ''}`);
              if (tag.eventName) console.log(`    Event: ${tag.eventName}`);
              if (tag.value) console.log(`    Value: ${tag.value} ${tag.currency || ''}`);
            });
            console.log(`\n💡 Recommendations:`);
            trackingAnalysis.recommendations.forEach(rec => console.log(`  ${rec}`));
          }
          console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);
          
          return analysis;
          
        } catch (firecrawlError) {
          console.error('❌ Firecrawl scraping failed:', firecrawlError);
          throw new Error(`Website analysis failed: Firecrawl could not scrape the website. Please check the URL and try again. Error: ${(firecrawlError as Error).message}`);
        }
      } else {
        console.error('❌ Firecrawl not configured');
        throw new Error('Website analysis unavailable: Firecrawl API key not configured. Please contact your administrator to set up VITE_FIRECRAWL_API_KEY.');
      }
    } catch (error) {
      console.error('❌ Error analyzing landing page:', error);
      throw error; // Propagate error - NO FAKE DATA GENERATION!
    }
  },

  /**
   * Get language name from code
   */
  getLanguageName(code: string): string {
    const languageMap: Record<string, string> = {
      'de': 'German',
      'en': 'English',
      'fr': 'French',
      'es': 'Spanish',
      'it': 'Italian',
      'nl': 'Dutch',
      'pt': 'Portuguese',
      'pl': 'Polish',
      'cs': 'Czech',
      'ru': 'Russian',
      'zh': 'Chinese',
      'ja': 'Japanese'
    };
    return languageMap[code] || 'English';
  },

  /**
   * Estimate conversion value based on industry and type
   */
  estimateConversionValue(industry: string, conversionType: string): number {
    const values: Record<string, Record<string, number>> = {
      'Automotive': { booking: 600, contact: 400, download: 50 },
      'Car Dealer': { booking: 700, contact: 500, download: 50 },
      'Real Estate': { contact: 500, booking: 300, download: 50 },
      'E-commerce': { purchase: 75, signup: 10, contact: 25 },
      'SaaS': { signup: 150, download: 25, contact: 200 },
      'Professional Services': { contact: 300, booking: 200, download: 50 }
    };
    
    return values[industry]?.[conversionType] || 100;
  },

  /**
   * Get all campaigns for an organization
   * 
   * SMART SYNC ARCHITECTURE:
   * 1. Try to load from Firestore cache (fast)
   * 2. If connected to Live API, fetch latest and sync to Firestore
   * 3. Always return real-time data if available
   */
  async getCampaigns(orgId: string): Promise<GoogleAdsCampaign[]> {
    try {
      console.log('📊 Fetching campaigns for org:', orgId);
      
      // 1. Check for real API connection
      const connection = await this.checkConnection(orgId);
      const isLive = connection.isConnected && connection.customerId && connection.status === 'connected';
      
      if (isLive && connection.customerId) {
        console.log(`🚀 Organization is LIVE. Fetching real-time data from Google Ads API (${connection.customerId})...`);
        
        try {
          const apiResult = await googleAdsMccService.listClientCampaigns(connection.customerId);
          
          if (apiResult.success && apiResult.campaigns) {
            console.log(`✅ Fetched ${apiResult.campaigns.length} campaigns from Live API`);
            
            // Map API campaigns to our local schema
            // Note: Google Ads API uses ENABLED/PAUSED, we map to active/paused
            const liveCampaigns: GoogleAdsCampaign[] = apiResult.campaigns.map(c => ({
              id: c.id,
              orgId: orgId,
              name: c.name,
              type: c.type.toLowerCase() as any,
              status: this.mapGoogleAdsStatus(c.status),
              isDemo: false,
              dailyBudget: c.dailyBudget,
              currency: 'EUR',
              googleCampaignId: c.resourceName,
              metrics: {
                impressions: c.metrics.impressions,
                clicks: c.metrics.clicks,
                conversions: c.metrics.conversions,
                cost: c.metrics.cost,
                ctr: c.metrics.ctr,
                avgCpc: c.metrics.avgCpc,
                cpa: c.metrics.conversions > 0 ? c.metrics.cost / c.metrics.conversions : 0,
                roas: c.metrics.cost > 0 ? c.metrics.conversionValue / c.metrics.cost : 0
              },
              startDate: c.startDate ? new Date(c.startDate) : new Date(),
              updatedAt: new Date(),
              createdAt: new Date(), // Fallback
              lastSyncAt: new Date()
            }));

            // ASYNC SYNC: Update Firestore in the background
            // We don't await this to keep the response snappy
            this.syncCampaignsToFirestore(orgId, liveCampaigns).catch(err => 
              console.error('❌ Failed to sync campaigns to Firestore:', err)
            );

            return liveCampaigns;
          }
        } catch (apiError) {
          console.error('❌ Google Ads API fetch failed, falling back to Firestore cache:', apiError);
        }
      }

      // 2. Fallback: Load from Firestore cache
      console.log('📂 Loading campaigns from Firestore cache...');
      const campaignsRef = collection(db, 'organizations', orgId, 'googleAdsCampaigns');
      const q = query(campaignsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const cachedCampaigns = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          lastSyncAt: data.lastSyncAt?.toDate()
        } as GoogleAdsCampaign;
      });

      console.log(`✅ Loaded ${cachedCampaigns.length} campaigns from cache`);
      return cachedCampaigns;

    } catch (error) {
      console.error('Error fetching campaigns:', error);
      return [];
    }
  },

  /**
   * Sync a list of campaigns to Firestore
   */
  async syncCampaignsToFirestore(orgId: string, campaigns: GoogleAdsCampaign[]): Promise<void> {
    const batch = campaigns.map(async (campaign) => {
      const campaignRef = doc(db, 'organizations', orgId, 'googleAdsCampaigns', campaign.id);
      
      // Clean and prepare for Firestore
      const data = this.cleanForFirestore({
        ...campaign,
        startDate: Timestamp.fromDate(campaign.startDate),
        endDate: campaign.endDate ? Timestamp.fromDate(campaign.endDate) : null,
        updatedAt: Timestamp.now(),
        lastSyncAt: Timestamp.now()
      });

      return setDoc(campaignRef, data, { merge: true });
    });

    await Promise.all(batch);
    console.log(`💾 Synced ${campaigns.length} campaigns to Firestore`);
  },

  /**
   * Get a single campaign
   */
  async getCampaign(orgId: string, campaignId: string): Promise<GoogleAdsCampaign | null> {
    try {
      const campaignRef = doc(db, 'organizations', orgId, 'googleAdsCampaigns', campaignId);
      const campaignDoc = await getDoc(campaignRef);
      
      if (!campaignDoc.exists()) {
        // If not in cache, try fetching all to populate cache (dynamic sync)
        const all = await this.getCampaigns(orgId);
        return all.find(c => c.id === campaignId) || null;
      }
      
      const data = campaignDoc.data();
      const campaign = {
        id: campaignDoc.id,
        ...data,
        startDate: data.startDate?.toDate() || new Date(),
        endDate: data.endDate?.toDate(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastSyncAt: data.lastSyncAt?.toDate()
      } as GoogleAdsCampaign;

      // If campaign is old (> 1 hour), refresh it
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      if (campaign.lastSyncAt && campaign.lastSyncAt < oneHourAgo) {
        console.log(`🔄 Campaign ${campaignId} cache is stale (>1h). Refreshing...`);
        const all = await this.getCampaigns(orgId);
        return all.find(c => c.id === campaignId) || campaign;
      }

      return campaign;
    } catch (error) {
      console.error('Error fetching campaign:', error);
      return null;
    }
  },

  /**
   * Create a campaign (wrapper that routes to Search or PMax)
   * Used by SimpleCampaignWizard and other components
   */
  async createCampaign(orgId: string, customerId: string | undefined, draft: CampaignDraft): Promise<string> {
    try {
      console.log(`🚀 Creating ${draft.campaignType} campaign...`);
      
      let campaign: GoogleAdsCampaign;
      
      if (draft.campaignType === 'pmax') {
        campaign = await this.createPMaxCampaign(draft);
      } else {
        // Default to Search campaign
        campaign = await this.createSearchCampaign(draft);
      }
      
      console.log(`✅ Campaign created: ${campaign.id}`);
      return campaign.id;
      
    } catch (error) {
      console.error('❌ Error in createCampaign wrapper:', error);
      throw error;
    }
  },

  /**
   * Create a new Search campaign
   * Uses MCC service (Firebase Cloud Functions) for reliable API access
   */
  async createSearchCampaign(draft: CampaignDraft): Promise<GoogleAdsCampaign> {
    try {
      const connection = await this.checkConnection(draft.orgId);
      const isDemo = connection.status === 'demo' || !connection.isConnected;
      
      let googleCampaignId: string | undefined;
      let campaignResourceName: string | undefined;
      
      // Check if we have real API connection
      if (!isDemo && connection.customerId) {
        try {
          console.log('🚀 Creating campaign via MCC Cloud Function...');
          
          // Use MCC service instead of direct API client
          // This uses server-side MCC credentials that don't expire
          const apiResult = await googleAdsMccService.createSearchCampaign(
            connection.customerId,
            {
              campaignName: draft.campaignName,
              dailyBudget: draft.dailyBudget,
              startDate: draft.startDate,
              endDate: draft.endDate,
              biddingStrategy: draft.biddingStrategy,
              targetCpa: draft.targetCpa,
              targetRoas: draft.targetRoas,
              maxCpcBidLimit: draft.maxCpcBidLimit,
              locations: draft.locations,
              languages: draft.languages,
              keywords: (draft.searchSettings?.keywords || []).map(k => ({
                text: typeof k === 'string' ? k : k.text,
                matchType: typeof k === 'string' ? 'BROAD' : (k.matchType || 'BROAD'),
                maxCpc: typeof k === 'string' ? undefined : k.maxCpc
              })),
              headlines: draft.searchSettings?.headlines || [],
              descriptions: draft.searchSettings?.descriptions || [],
              finalUrl: draft.landingPageUrl,
              path1: draft.searchSettings?.displayUrlPath?.split('/')[0],
              path2: draft.searchSettings?.displayUrlPath?.split('/')[1]
            }
          );
          
          googleCampaignId = apiResult.campaignResourceName;
          campaignResourceName = apiResult.campaignResourceName;
          
          console.log('✅ Campaign created via MCC Cloud Function!', apiResult.message);
        } catch (apiError) {
          console.error('❌ MCC API error, falling back to demo mode:', apiError);
          // Fall back to demo mode if API fails
        }
      }
      
      // Generate local campaign ID
      const campaignId = `campaign_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const campaign: GoogleAdsCampaign = {
        id: campaignId,
        orgId: draft.orgId,
        name: draft.campaignName,
        type: 'search',
        status: 'draft',
        isDemo: !googleCampaignId, // Not demo if we have Google ID
        dailyBudget: draft.dailyBudget,
        totalBudget: draft.totalBudget,
        currency: 'EUR',
        locations: draft.locations,
        languages: draft.languages,
        startDate: draft.startDate,
        endDate: draft.endDate,
        biddingStrategy: draft.biddingStrategy,
        targetCpa: draft.targetCpa,
        targetRoas: draft.targetRoas,
        googleCampaignId, // Store Google's resource name
        analysis: draft.analysis, // ✅ Store full analysis for later use (ad variations, etc.)
        searchSettings: {
          adGroups: draft.searchSettings?.adGroups || [],
          keywords: draft.searchSettings?.keywords || [],
          negativeKeywords: draft.searchSettings?.negativeKeywords || [],
          headlines: draft.searchSettings?.headlines || [],
          descriptions: draft.searchSettings?.descriptions || [],
          displayUrlPath: draft.searchSettings?.displayUrlPath || '',
          conversionGoals: draft.searchSettings?.conversionGoals || []
        },
        metrics: {
          impressions: 0,
          clicks: 0,
          conversions: 0,
          cost: 0,
          ctr: 0,
          cpa: 0,
          roas: 0,
          avgCpc: 0
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Validate API v22 compliance before saving
      try {
        this.validateAPIv22Compliance(campaign);
      } catch (validationError) {
        console.error('⚠️ API v22 validation warning:', validationError);
        // Log warning but don't fail - allow demo campaigns to be saved
      }
      
      // Save to Firestore
      const campaignRef = doc(db, 'organizations', draft.orgId, 'googleAdsCampaigns', campaignId);
      
      // Clean all undefined fields for Firestore (Firestore doesn't accept undefined)
      const campaignData = this.cleanForFirestore({
        ...campaign,
        startDate: Timestamp.fromDate(campaign.startDate),
        endDate: campaign.endDate ? Timestamp.fromDate(campaign.endDate) : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      console.log('💾 Saving campaign to Firestore (cleaned for storage)...');
      await setDoc(campaignRef, campaignData);
      
      console.log(`✅ Search campaign created: ${campaign.name} (Demo: ${campaign.isDemo})`);
      return campaign;
    } catch (error) {
      console.error('Error creating search campaign:', error);
      throw new Error('Failed to create search campaign');
    }
  },

  /**
   * Create a new Performance Max campaign
   */
  async createPMaxCampaign(draft: CampaignDraft): Promise<GoogleAdsCampaign> {
    try {
      const connection = await this.checkConnection(draft.orgId);
      const isDemo = connection.status === 'demo' || !connection.isConnected;
      
      let googleCampaignId: string | undefined;
      
      // Check if we have real API connection
      if (!isDemo && connection.customerId) {
        try {
          console.log('🚀 Creating PMax campaign via Google Ads API v22...');
          
          // Validate: PMax requires conversion goals
          if (!draft.pmaxSettings?.conversionGoals || draft.pmaxSettings.conversionGoals.length === 0) {
            throw new Error('Performance Max campaigns require at least one conversion goal');
          }
          
          // Create campaign via real API
          const apiResult = await googleAdsApiClient.createCompletePMaxCampaign(
            draft.orgId,
            connection.customerId,
            draft,
            (step, current, total) => {
              console.log(`Progress: ${current}/${total} - ${step}`);
            }
          );
          
          googleCampaignId = apiResult.campaignResourceName;
          
          console.log('✅ PMax campaign created via Google Ads API!');
          
          // Set conversion goals (REQUIRED for PMax)
          try {
            console.log(`🎯 Setting ${draft.pmaxSettings.conversionGoals.length} conversion goals (REQUIRED for PMax)...`);
            await googleAdsApiClient.setCampaignConversionGoals(
              draft.orgId,
              connection.customerId,
              googleCampaignId,
              draft.pmaxSettings.conversionGoals
            );
            console.log('✅ Conversion goals set for PMax campaign');
          } catch (convError) {
            console.error('❌ Failed to set conversion goals for PMax:', convError);
            // This is critical for PMax, so we should warn but not fail entirely
            console.warn('⚠️ PMax campaign created but conversion goals failed. Campaign may not perform optimally.');
          }
        } catch (apiError) {
          console.error('❌ Google Ads API error, falling back to demo mode:', apiError);
          // Fall back to demo mode if API fails
        }
      }
      
      const campaignId = `campaign_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const campaign: GoogleAdsCampaign = {
        id: campaignId,
        orgId: draft.orgId,
        name: draft.campaignName,
        type: 'pmax',
        status: 'draft',
        isDemo: !googleCampaignId, // Not demo if we have Google ID
        googleCampaignId, // Store Google's resource name
        dailyBudget: draft.dailyBudget,
        totalBudget: draft.totalBudget,
        currency: 'EUR',
        locations: draft.locations,
        languages: draft.languages,
        startDate: draft.startDate,
        endDate: draft.endDate,
        biddingStrategy: draft.biddingStrategy,
        targetCpa: draft.targetCpa,
        targetRoas: draft.targetRoas,
        analysis: draft.analysis, // ✅ Store full analysis for later use (ad variations, etc.)
        pmaxSettings: {
          assetGroups: draft.pmaxSettings?.assetGroups || [],
          conversionGoals: draft.pmaxSettings?.conversionGoals || [],
          audienceSignals: draft.pmaxSettings?.audienceSignals || []
        },
        metrics: {
          impressions: 0,
          clicks: 0,
          conversions: 0,
          cost: 0,
          ctr: 0,
          cpa: 0,
          roas: 0,
          avgCpc: 0
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Validate API v22 compliance before saving
      try {
        this.validateAPIv22Compliance(campaign);
      } catch (validationError) {
        console.error('⚠️ API v22 validation warning:', validationError);
        // Log warning but don't fail - allow demo campaigns to be saved
      }
      
      // Save to Firestore
      const campaignRef = doc(db, 'organizations', draft.orgId, 'googleAdsCampaigns', campaignId);
      
      // Clean all undefined fields for Firestore (Firestore doesn't accept undefined)
      const campaignData = this.cleanForFirestore({
        ...campaign,
        startDate: Timestamp.fromDate(campaign.startDate),
        endDate: campaign.endDate ? Timestamp.fromDate(campaign.endDate) : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      console.log('💾 Saving PMax campaign to Firestore (cleaned for storage)...');
      await setDoc(campaignRef, campaignData);
      
      console.log(`✅ PMax campaign created: ${campaign.name} (Demo: ${isDemo})`);
      return campaign;
    } catch (error) {
      console.error('Error creating PMax campaign:', error);
      throw new Error('Failed to create PMax campaign');
    }
  },

  /**
   * Update campaign status
   */
  async updateCampaignStatus(
    orgId: string, 
    campaignId: string, 
    status: CampaignStatus
  ): Promise<void> {
    try {
      const campaignRef = doc(db, 'organizations', orgId, 'googleAdsCampaigns', campaignId);
      
      await updateDoc(campaignRef, {
        status,
        updatedAt: Timestamp.now()
      });
      
      console.log(`✅ Campaign ${campaignId} status updated to ${status}`);
    } catch (error) {
      console.error('Error updating campaign status:', error);
      throw new Error('Failed to update campaign status');
    }
  },

  /**
   * Pause a campaign
   */
  async pauseCampaign(orgId: string, campaignId: string): Promise<void> {
    return this.updateCampaignStatus(orgId, campaignId, 'paused');
  },

  /**
   * Resume a campaign
   */
  async resumeCampaign(orgId: string, campaignId: string): Promise<void> {
    return this.updateCampaignStatus(orgId, campaignId, 'active');
  },

  /**
   * Delete a campaign
   */
  async deleteCampaign(orgId: string, campaignId: string): Promise<void> {
    try {
      const campaignRef = doc(db, 'organizations', orgId, 'googleAdsCampaigns', campaignId);
      await deleteDoc(campaignRef);
      
      console.log(`✅ Campaign ${campaignId} deleted`);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      throw new Error('Failed to delete campaign');
    }
  },

  /**
   * Update campaign
   */
  async updateCampaign(
    orgId: string, 
    campaignId: string, 
    updates: Partial<GoogleAdsCampaign>
  ): Promise<void> {
    try {
      const campaignRef = doc(db, 'organizations', orgId, 'googleAdsCampaigns', campaignId);
      
      // Convert dates to Timestamps
      const updateData: any = { ...updates };
      if (updates.startDate) {
        updateData.startDate = Timestamp.fromDate(updates.startDate);
      }
      if (updates.endDate) {
        updateData.endDate = Timestamp.fromDate(updates.endDate);
      }
      updateData.updatedAt = Timestamp.now();
      
      await updateDoc(campaignRef, updateData);
      
      console.log(`✅ Campaign ${campaignId} updated`);
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw new Error('Failed to update campaign');
    }
  },

  /**
   * Generate AI optimization suggestions for a campaign
   */
  async generateOptimizations(
    orgId: string, 
    campaignId: string
  ): Promise<OptimizationSuggestion[]> {
    try {
      const campaign = await this.getCampaign(orgId, campaignId);
      if (!campaign) {
        throw new Error('Campaign not found');
      }
      
      // Use OpenAI to generate suggestions
      const suggestions = await openaiService.generateCampaignOptimizations(campaign);
      
      return suggestions;
    } catch (error) {
      console.error('Error generating optimizations:', error);
      
      // Fallback: Return mock suggestions
      return this.getMockOptimizations(campaignId);
    }
  },

  /**
   * Mock optimization suggestions for demo
   */
  getMockOptimizations(campaignId: string): OptimizationSuggestion[] {
    return [
      {
        id: `opt_${Date.now()}_1`,
        campaignId,
        type: 'budget',
        priority: 'high',
        title: 'Increase Daily Budget',
        description: 'Your campaign is limited by budget. Increasing it could improve reach.',
        currentValue: '50 €/Tag',
        suggestedValue: '75 €/Tag',
        potentialImpact: '+25% impressions',
        confidence: 85,
        createdAt: new Date(),
        applied: false
      },
      {
        id: `opt_${Date.now()}_2`,
        campaignId,
        type: 'keyword',
        priority: 'medium',
        title: 'Add Negative Keywords',
        description: 'Block irrelevant searches to improve quality score.',
        currentValue: '5 negative keywords',
        suggestedValue: '12 negative keywords',
        potentialImpact: '+10% CTR',
        confidence: 75,
        createdAt: new Date(),
        applied: false
      },
      {
        id: `opt_${Date.now()}_3`,
        campaignId,
        type: 'ad_copy',
        priority: 'medium',
        title: 'Test New Ad Variations',
        description: 'Create additional ad copy to improve performance.',
        potentialImpact: '+15% conversions',
        confidence: 70,
        createdAt: new Date(),
        applied: false
      }
    ];
  },

  /**
   * Simulate campaign performance update (for demo mode)
   */
  async simulatePerformanceUpdate(orgId: string, campaignId: string): Promise<void> {
    try {
      const campaign = await this.getCampaign(orgId, campaignId);
      if (!campaign || !campaign.isDemo) {
        return;
      }
      
      // Generate realistic performance metrics
      const dailyBudget = campaign.dailyBudget;
      const avgCpc = 1.5 + Math.random() * 1.5; // 1,50 – 3,00 €
      const clicks = Math.floor((dailyBudget / avgCpc) * (0.8 + Math.random() * 0.4));
      const impressions = Math.floor(clicks / (0.02 + Math.random() * 0.03)); // 2-5% CTR
      const conversions = Math.floor(clicks * (0.02 + Math.random() * 0.03)); // 2-5% conversion rate
      const cost = clicks * avgCpc;
      const ctr = (clicks / impressions) * 100;
      const cpa = conversions > 0 ? cost / conversions : 0;
      
      const updatedMetrics = {
        impressions: campaign.metrics.impressions + impressions,
        clicks: campaign.metrics.clicks + clicks,
        conversions: campaign.metrics.conversions + conversions,
        cost: campaign.metrics.cost + cost,
        ctr,
        avgCpc,
        cpa,
        roas: 3.5 + Math.random() * 1.5 // 3.5-5.0 ROAS
      };
      
      await this.updateCampaign(orgId, campaignId, { metrics: updatedMetrics });
      
      console.log(`✅ Demo performance updated for campaign ${campaignId}`);
    } catch (error) {
      console.error('Error simulating performance:', error);
    }
  },

  /**
   * Analyze multiple pages for conversions
   */
  async analyzeMultiplePages(
    pages: string[],
    onProgress?: (current: number, total: number, page: string) => void
  ): Promise<DetectedConversion[]> {
    const allConversions: DetectedConversion[] = [];
    
    for (let i = 0; i < pages.length; i++) {
      const pageUrl = pages[i];
      onProgress?.(i + 1, pages.length, pageUrl);
      
      try {
        console.log(`📄 Analyzing page ${i + 1}/${pages.length}: ${pageUrl}`);
        
        // Scrape page
        const scrapedData = await firecrawlService.scrapeWebsite(pageUrl);
        
        // Analyze with Gemini
        const analysis = await geminiService.analyzeLandingPage(
          scrapedData.html,
          scrapedData.markdown,
          pageUrl,
          scrapedData.metadata
        );
        
        // Add conversions with page URL
        if (analysis.detectedConversions && analysis.detectedConversions.length > 0) {
          analysis.detectedConversions.forEach(conv => {
            allConversions.push({
              ...conv,
              pageUrl: pageUrl,  // ✅ Add URL where conversion was found
              evidence: `${conv.evidence} (found on ${new URL(pageUrl).pathname})`
            });
          });
          
          console.log(`  ✅ Found ${analysis.detectedConversions.length} conversions on this page`);
        }
        
      } catch (error) {
        console.error(`  ❌ Failed to analyze ${pageUrl}:`, error);
      }
    }
    
    // Deduplicate conversions (same type, different pages)
    const deduped = this.deduplicateConversions(allConversions);
    
    console.log(`✅ Total: ${deduped.length} unique conversions found across ${pages.length} pages`);
    return deduped;
  },

  /**
   * Deduplicate conversions while keeping page URLs
   */
  deduplicateConversions(conversions: DetectedConversion[]): DetectedConversion[] {
    const grouped = new Map<string, DetectedConversion>();
    
    conversions.forEach(conv => {
      const key = `${conv.type}-${conv.name.toLowerCase()}`;
      
      if (!grouped.has(key)) {
        grouped.set(key, { ...conv, foundOnPages: [conv.pageUrl!] });
      } else {
        const existing = grouped.get(key)!;
        if (!existing.foundOnPages!.includes(conv.pageUrl!)) {
          existing.foundOnPages!.push(conv.pageUrl!);
        }
        // Keep higher confidence
        if (conv.confidence > existing.confidence) {
          existing.confidence = conv.confidence;
          existing.evidence = conv.evidence;
        }
      }
    });
    
    return Array.from(grouped.values());
  },

  /**
   * Remove undefined fields from object for Firestore
   * Firestore doesn't accept undefined values - use null or omit field
   * Also ensures API v22 compliance for future Google Ads transfer
   */
  cleanForFirestore(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanForFirestore(item));
    }
    
    // Preserve Timestamp and Date objects
    if (obj instanceof Timestamp || obj instanceof Date) {
      return obj;
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      
      for (const key in obj) {
        const value = obj[key];
        
        // Skip undefined fields entirely (don't save to Firestore)
        if (value !== undefined) {
          cleaned[key] = this.cleanForFirestore(value);
        }
      }
      
      return cleaned;
    }
    
    return obj;
  },

  /**
   * Validate campaign complies with Google Ads API v22
   * Ensures campaigns can be transferred to Google Ads API later
   */
  validateAPIv22Compliance(campaign: GoogleAdsCampaign): void {
    console.log('🔍 Validating API v22 compliance...');
    
    // Check minimum requirements
    if (campaign.type === 'search') {
      const headlines = campaign.searchSettings?.headlines || [];
      const descriptions = campaign.searchSettings?.descriptions || [];
      
      if (headlines.length < 3) {
        throw new Error('API v22 requires minimum 3 headlines for Search campaigns');
      }
      if (descriptions.length < 2) {
        throw new Error('API v22 requires minimum 2 descriptions for Search campaigns');
      }
      
      // Check character limits (strict)
      headlines.forEach((h, i) => {
        if (h.length > 30) {
          throw new Error(`Headline ${i + 1} exceeds 30 chars: "${h}" (${h.length} chars) - API v22 violation`);
        }
      });
      
      descriptions.forEach((d, i) => {
        if (d.length > 90) {
          throw new Error(`Description ${i + 1} exceeds 90 chars: "${d}" (${d.length} chars) - API v22 violation`);
        }
      });
      
      console.log(`✅ Search Campaign API v22 valid: ${headlines.length} headlines, ${descriptions.length} descriptions`);
    }
    
    if (campaign.type === 'pmax') {
      // PMax validation
      const assetGroups = campaign.pmaxSettings?.assetGroups || [];
      
      if (assetGroups.length === 0) {
        throw new Error('API v22 requires at least 1 asset group for PMax campaigns');
      }
      
      const headlines = assetGroups[0]?.headlines || [];
      const descriptions = assetGroups[0]?.descriptions || [];
      
      if (headlines.length < 3) {
        throw new Error('API v22 requires minimum 3 headlines for PMax campaigns');
      }
      if (descriptions.length < 2) {
        throw new Error('API v22 requires minimum 2 descriptions for PMax campaigns');
      }
      
      // Check character limits
      headlines.forEach((h, i) => {
        if (h.length > 30) {
          throw new Error(`PMax Headline ${i + 1} exceeds 30 chars - API v22 violation`);
        }
      });
      
      descriptions.forEach((d, i) => {
        if (d.length > 90) {
          throw new Error(`PMax Description ${i + 1} exceeds 90 chars - API v22 violation`);
        }
      });
      
      console.log(`✅ PMax Campaign API v22 valid: ${headlines.length} headlines, ${descriptions.length} descriptions`);
    }
  }
};

